import { Component, OnInit } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { ChartConfiguration } from 'chart.js';
import { ApiService } from '../../../_core/_services/api.service';
import {
  AdminFinancePnlReportDto,
  FinanceReferenceRateDto,
  FinanceReportFilters,
  TradeProfitSnapshotListItemDto,
  UpsertFinanceReferenceRateDto
} from '../../../_core/_model/finance-report';
import { ApiResponse } from '../../../_core/_model/api-response';

@Component({
  selector: 'app-admin-finance-dashboard',
  standalone: false,
  templateUrl: './admin-finance-dashboard.html',
  styleUrls: ['./admin-finance-dashboard.css']
})

export class AdminFinanceDashboard implements OnInit {
  readonly today = new Date().toISOString().slice(0, 10);

  loading = false;
  loadingTrades = false;
  exporting = false;
  savingRate = false;

  report?: AdminFinancePnlReportDto;
  trades: TradeProfitSnapshotListItemDto[] = [];
  rates: FinanceReferenceRateDto[] = [];

  filters: FinanceReportFilters = {
    rowsPerPage: 20,
    page: 1
  };

  rateForm: UpsertFinanceReferenceRateDto = {
    currencyCode: 'NGN',
    rateDate: this.today,
    rateToUsd: 0,
    source: 'manual',
    isFallback: false
  };

  profitTrendChart?: ChartConfiguration<'line'>['data'];
  spreadByCurrencyChart?: ChartConfiguration<'bar'>['data'];
  profitByPairChart?: ChartConfiguration<'bar'>['data'];

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.loadReport();
    this.loadTrades();
    this.loadRates();
  }

  loadReport(): void {
    this.loading = true;

    this.api.getAdminFinancePnlReport(this.filters).subscribe({
      next: (res: ApiResponse<AdminFinancePnlReportDto>) => {
        if (res.success) {
          this.report = res.data || undefined;
          this.buildCharts();
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadTrades(): void {
    this.loadingTrades = true;

    this.api.getAdminFinanceTradeSnapshots(this.filters).subscribe({
      next: (res: ApiResponse<TradeProfitSnapshotListItemDto[]>) => {
        if (res.success) {
          this.trades = res.data || [];
        }
        this.loadingTrades = false;
      },
      error: () => {
        this.loadingTrades = false;
      }
    });
  }

  loadRates(): void {
    this.api.getFinanceReferenceRates({}).subscribe({
      next: (res: ApiResponse<FinanceReferenceRateDto[]>) => {
        if (res.success) {
          this.rates = res.data || [];
        }
      }
    });
  }

  applyFilters(): void {
    this.filters.page = 1;
    this.loadAll();
  }

  resetFilters(): void {
    this.filters = {
      page: 1,
      rowsPerPage: 20
    };
    this.loadAll();
  }

  exportCsv(): void {
    this.exporting = true;

    this.api.exportAdminFinanceTradeSnapshots(this.filters).subscribe({
      next: (response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const fileName = this.getFileNameFromResponse(response) || 'swipfx-finance-trade-snapshots.csv';

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');

        anchor.href = url;
        anchor.download = fileName;
        anchor.click();

        window.URL.revokeObjectURL(url);
        this.exporting = false;
      },
      error: () => {
        this.exporting = false;
      }
    });
  }

  saveRate(): void {
    this.savingRate = true;

    this.api.saveFinanceReferenceRate(this.rateForm).subscribe({
      next: (res: ApiResponse<FinanceReferenceRateDto>) => {
        if (res.success) {
          this.loadRates();
          this.loadReport();
        }
        this.savingRate = false;
      },
      error: () => {
        this.savingRate = false;
      }
    });
  }

  private buildCharts(): void {
    if (!this.report) {
      return;
    }

    this.profitTrendChart = {
      labels: this.report.profitTrend.map(x => this.formatShortDate(x.date)),
      datasets: [
        {
          label: 'Net trading profit USD',
          data: this.report.profitTrend.map(x => x.netTradingProfitUsd)
        },
        {
          label: 'Gross spread USD',
          data: this.report.profitTrend.map(x => x.grossSpreadUsd)
        },
        {
          label: 'Fee leakage USD',
          data: this.report.profitTrend.map(x => x.feeLeakageUsd)
        }
      ]
    };

    this.spreadByCurrencyChart = {
      labels: this.report.spreadByCurrency.map(x => x.currencyCode),
      datasets: [
        {
          label: 'Gross spread USD',
          data: this.report.spreadByCurrency.map(x => x.grossSpreadUsd)
        }
      ]
    };

    this.profitByPairChart = {
      labels: this.report.profitByPair.map(x => x.currencyPair),
      datasets: [
        {
          label: 'Net profit USD',
          data: this.report.profitByPair.map(x => x.netTradingProfitUsd)
        }
      ]
    };
  }

  formatMoney(value?: number | null, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(value || 0);
  }

  formatNumber(value?: number | null): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 8
    }).format(value || 0);
  }

  formatShortDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleDateString();
  }

  private getFileNameFromResponse(response: HttpResponse<Blob>): string | null {
    const disposition = response.headers?.get('content-disposition');

    if (!disposition) {
      return null;
    }

    const match = /filename="?([^"]+)"?/i.exec(disposition);
    return match ? match[1] : null;
  }
}
